import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react';

import useUser from 'hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import useUI from 'hooks/use-ui';
import EmptyLoadingState from 'components/empty-loading-state';
import IframeHeightOverride from 'components/core/iframe-height-override';
import useEnterpriseDutchieLoyalty from 'hooks/use-enterprise-dutchie-loyalty';
import { useLoyaltyEnrollmentAnalytics } from 'src/hooks/use-loyalty-enrollment-analytics';
import { Integrations } from 'src/components/loyalty/use-loyalty/types';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { useSearchEnterprise } from '../hooks/use-search-enterprise';
import { useEarlyThemeDispensary } from '../hooks/use-early-theme-dispensary';
import { useEnterpriseSignupCustomBranding } from '../hooks/use-enterprise-signup-custom-branding';
import { LandingPage } from './components/landing-page';
import { LoginMethodSelection } from './components/login-method-selection';
import { EnterpriseSignupContent } from './components/enterprise-signup-content';
import { LoyaltyRewards } from './components/loyalty-rewards';
import type { FlowStep } from './types';
import { FLOW_STEPS } from './types';

export function EnterpriseSignup(): JSX.Element {
  const router = useRouter();
  const { enterpriseId, cName, state: stateFromUrl, embed: embedParam } = router?.query ?? {};
  const embedParamString = Array.isArray(embedParam) ? embedParam[0] : embedParam;
  const isEmbedMode = embedParamString === '1';
  const User = useUser();
  const showErnie = useErnie();
  const UI = useUI();

  // Flow state - 4 step flow: landing -> login-method -> email-signup -> loyalty-rewards
  const [currentStep, setCurrentStep] = useState<FlowStep>(FLOW_STEPS.LANDING);
  const [selectedState, setSelectedState] = useState('');
  const [canonicalEnterpriseId, setCanonicalEnterpriseId] = useState('');
  // POS-17999 -- best-guess region for theming only, known before the user confirms a state.
  const [detectedState, setDetectedState] = useState('');

  const [loyaltyEnrollmentComplete, setLoyaltyEnrollmentComplete] = useState(false);
  const [enrollmentAttempted, setEnrollmentAttempted] = useState(false);

  // EmailCodeLoginForm logs new users in (isLoggedIn/userId flip true) at the code-verification
  // step, before they've completed the CompleteAccount profile step (name/phone/birthday/medical
  // card) -- unlike the old SignUpForm, which created the account and collected the profile in one
  // atomic action. Without this, the enrollment effect below (which only checked isLoggedIn) fired
  // the instant a new user verified their code, enrolling them with an empty profile and yanking
  // them out of the profile step before they ever saw it. requiresLoginFlow/loginFlowComplete track
  // whether the user actually had to go through this page's own login flow -- if they were already
  // logged in when they clicked "start earning rewards", there's no profile step to wait for, so
  // enrollment proceeds exactly as before.
  const [requiresLoginFlow, setRequiresLoginFlow] = useState(false);
  const [loginFlowComplete, setLoginFlowComplete] = useState(false);

  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const userLoading = useObserver(() => User.loading);
  const userId = useObserver(() => User.id);

  const { trackViewed, trackSubmitted, trackSucceeded, trackFailed } = useLoyaltyEnrollmentAnalytics({
    surface: 'enterpriseSignup',
    integration: Integrations.dutchiepos,
  });
  const hasTrackedViewedRef = useRef(false);
  useEffect(() => {
    if (hasTrackedViewedRef.current) {
      return;
    }
    hasTrackedViewedRef.current = true;
    trackViewed();
  }, [trackViewed]);

  // Convert enterpriseId (or cName slug) to string for template usage
  const enterpriseIdRaw = enterpriseId ?? cName ?? '';
  const enterpriseIdString = Array.isArray(enterpriseIdRaw) ? enterpriseIdRaw[0] : enterpriseIdRaw;

  const { enterprise, loading: enterpriseLoading, error: enterpriseError } = useSearchEnterprise({
    enterpriseId: enterpriseIdString,
  });
  const { customBrandingEnabled } = useEnterpriseSignupCustomBranding({
    enterpriseId: enterpriseIdString,
  });

  useEffect(() => {
    if (enterprise?.uniqueName && enterpriseIdString !== enterprise.uniqueName) {
      const slug = enterprise.uniqueName;
      const isStoresEnterpriseRoute = router?.pathname.startsWith('/stores/enterprise/');
      const isEnterpriseSignupRoute = router?.pathname.startsWith('/enterprise/');
      // Preserve embed=1 through the canonical-slug redirect — otherwise isEmbedMode would flip
      // to false mid-flow, dropping resize wrapping and falling back to top-level navigation.
      const embedSuffix = isEmbedMode ? '?embed=1' : '';

      if (isStoresEnterpriseRoute) {
        void router?.replace(`/stores/enterprise/${slug}/signup${embedSuffix}`, undefined, { shallow: true });
      } else if (isEnterpriseSignupRoute) {
        void router?.replace(`/enterprise/${slug}/signup${embedSuffix}`, undefined, { shallow: true });
      }
    }
    if (enterprise?.enterpriseId && enterpriseIdString !== enterprise.enterpriseId) {
      setCanonicalEnterpriseId(enterprise.enterpriseId);
    }
  }, [enterprise?.uniqueName, enterprise?.enterpriseId, enterpriseId, enterpriseIdString, router, isEmbedMode]);

  useEffect(() => {
    if (router?.isReady && stateFromUrl) {
      const stateParam = Array.isArray(stateFromUrl) ? stateFromUrl[0] : stateFromUrl;
      if (stateParam && stateParam !== selectedState) {
        setSelectedState(stateParam);
      }
    }
  }, [router?.isReady, stateFromUrl, selectedState]);

  const {
    createEnterpriseDutchiePosLoyaltyMember,
    loading: loyaltyLoading,
    error: loyaltyError,
  } = useEnterpriseDutchieLoyalty();

  // Use canonical enterprise ID if available, otherwise use the one from URL
  const activeEnterpriseId = canonicalEnterpriseId || enterpriseIdString;

  // Handle loyalty enrollment errors
  useEffect(() => {
    if (loyaltyError) {
      console.error('Loyalty enrollment error:', loyaltyError);
      showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
      if (isEmbedMode) {
        UI.embeddedEventDispatcher('loyalty:error', { message: loyaltyError.message });
      }
    }
  }, [loyaltyError, showErnie, isEmbedMode, UI]);

  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const readyToEnroll = isLoggedIn && (!requiresLoginFlow || loginFlowComplete);
  const skip = !selectedState || (!userId && !birthday && !isLoggedIn);
  const skipRewards = skip || !phone;
  // Nothing to configure until the profile exists, so wait for the login flow -- just as
  // skipRewards waits for a phone number. Running this mid-flow is what unmounted the form.
  const skipLoyaltyConfig = skip || (requiresLoginFlow && !loginFlowComplete);

  const { data: rewards, loading: rewardsDataLoadingResult, refetch: refetchRewards } = useGetRewards({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    phone: phone ?? '',
    birthday: birthday ?? '',
    pin: 'skip',
    customerId: userId,
    skip: skipRewards,
  });

  const {
    data: loyaltyConfigData,
    loading: loyaltyConfigLoadingResult,
    refetch: refetchLoyaltyConfig,
  } = useGetDutchiePosLoyaltyConfig({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    skip: skipLoyaltyConfig,
  });

  const dispensaryId = loyaltyConfigData?.dispensaryId;
  const { dispensary: themeDispensary } = useDispensary(dispensaryId ?? undefined);

  // POS-17999 -- themeDispensary above needs a confirmed state + known user, so it's never
  // available on first render. This fills that gap from detectedState, and steps aside once
  // themeDispensary resolves.
  const earlyThemeDispensary = useEarlyThemeDispensary({
    enterpriseId: activeEnterpriseId,
    regionCode: detectedState,
    enabled: customBrandingEnabled && !themeDispensary,
  });
  // Preserve the authoritative dispensary regardless of the rollout flag; only the new early
  // lookup is gated. Downstream signup flow already relied on this once the real config resolved.
  const effectiveThemeDispensary = themeDispensary ?? earlyThemeDispensary;

  const hasFiredLoadedEventRef = useRef(false);

  useEffect(() => {
    if (isEmbedMode && !hasFiredLoadedEventRef.current) {
      hasFiredLoadedEventRef.current = true;
      UI.embeddedEventDispatcher('loyalty:loaded', {});
    }
  }, [isEmbedMode, UI]);

  useEffect(() => {
    if (effectiveThemeDispensary) {
      UI.setEnterpriseDispensary(effectiveThemeDispensary);
    }
    return () => {
      UI.setEnterpriseDispensary(null);
    };
  }, [UI, effectiveThemeDispensary]);

  // Refetch hooks when selectedState changes
  useEffect(() => {
    if (userId && selectedState && isLoggedIn && phone && birthday) {
      void refetchRewards();
      void refetchLoyaltyConfig();
    }
  }, [selectedState, isLoggedIn, refetchRewards, refetchLoyaltyConfig, userId, phone, birthday]);

  // Enroll user in loyalty when they become logged in and have selected a state
  useEffect(() => {
    const enrollUserInLoyalty = async (): Promise<void> => {
      if (
        !readyToEnroll ||
        !userId ||
        !selectedState ||
        userLoading ||
        loyaltyLoading ||
        loyaltyEnrollmentComplete ||
        enrollmentAttempted ||
        enterpriseLoading ||
        !enterprise
      ) {
        return;
      }
      setEnrollmentAttempted(true);

      // If user is already a loyalty member, set the enrollment complete flag and show the success message
      if (rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        setLoyaltyEnrollmentComplete(true);
        showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');
        // Move to loyalty-rewards and enroll
        if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
          setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
        }
      }

      if (!rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        trackSubmitted();
        try {
          // request rewards and loylatyConfig after each state selection and then run this call or not

          await createEnterpriseDutchiePosLoyaltyMember({
            variables: {
              enterpriseId: activeEnterpriseId,
              userId,
              isoRegionCode: `US-${selectedState}`,
            },
          });
          void refetchRewards();

          trackSucceeded();
          setLoyaltyEnrollmentComplete(true);
          showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');

          // Move to loyalty-rewards and enroll
          if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
            setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
          }
        } catch (error) {
          trackFailed();
          // Don't dispatch loyalty:error here — createEnterpriseDutchiePosLoyaltyMember failing
          // also populates loyaltyError below, which already dispatches. Dispatching here too
          // would send the embedding parent two error events for one failure.
          console.error('Error enrolling in enterprise loyalty:', error);
          showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
        }
      }
    };

    if (userLoading) {
      return;
    }
    if (
      readyToEnroll &&
      selectedState &&
      !loyaltyEnrollmentComplete &&
      !enrollmentAttempted &&
      !rewardsDataLoadingResult &&
      !loyaltyConfigLoadingResult
    ) {
      void enrollUserInLoyalty();
    }
  }, [
    loyaltyConfigData,
    rewards?.getRewardsV2,
    rewardsDataLoadingResult,
    loyaltyConfigLoadingResult,
    readyToEnroll,
    userLoading,
    userId,
    selectedState,
    currentStep,
    loyaltyLoading,
    loyaltyEnrollmentComplete,
    enrollmentAttempted,
    enterpriseLoading,
    enterprise,
    activeEnterpriseId,
    createEnterpriseDutchiePosLoyaltyMember,
    showErnie,
    refetchRewards,
    trackSubmitted,
    trackSucceeded,
    trackFailed,
  ]);

  const handleStartEarningRewards = (stateCode: string): void => {
    setSelectedState(stateCode);

    if (router) {
      void router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, state: stateCode },
        },
        undefined,
        { shallow: true }
      );
    }

    // If not logged in, go to login flow
    // If already logged in, the enrollment effect will handle moving to loyalty-rewards
    if (!isLoggedIn) {
      setRequiresLoginFlow(true);
      setCurrentStep(FLOW_STEPS.LOGIN_METHOD);
    }
  };

  const handleContinueWithEmail = (): void => {
    setCurrentStep(FLOW_STEPS.EMAIL_SIGNUP);
  };

  const handleLoginFlowSuccess = (): void => {
    setLoginFlowComplete(true);
  };

  let content: JSX.Element;

  // EnterpriseSignupContent keeps `step` in local state, so replacing it with a spinner mid-flow
  // resets the user to the email form. Keyed on currentStep, not loginFlowComplete: only a
  // *successful* enrollment advances the step, so releasing on loginFlowComplete would unmount
  // during enrollment and strand a failed one back on the email form.
  const inLoginFlow =
    requiresLoginFlow && (currentStep === FLOW_STEPS.LOGIN_METHOD || currentStep === FLOW_STEPS.EMAIL_SIGNUP);
  const showLoadingState = rewardsDataLoadingResult || loyaltyConfigLoadingResult || loyaltyLoading;

  if (enterpriseLoading || !enterprise || (!inLoginFlow && showLoadingState)) {
    content = <EmptyLoadingState isLoading page='enterprise-signup' />;
  } else if (enterpriseError) {
    console.error('Enterprise error:', {
      enterpriseError,
      enterpriseId: enterpriseIdString,
    });
    content = <EmptyLoadingState isLoading={false} page='enterprise-signup' />;
  } else if (currentStep === FLOW_STEPS.LANDING) {
    content = (
      <LandingPage
        enterprise={enterprise}
        onStartEarningRewards={handleStartEarningRewards}
        onLikelyStateDetected={setDetectedState}
        themeDispensary={effectiveThemeDispensary}
        customBrandingEnabled={customBrandingEnabled}
      />
    );
  } else if (currentStep === FLOW_STEPS.LOGIN_METHOD) {
    content = <LoginMethodSelection onContinueWithEmail={handleContinueWithEmail} />;
  } else if (currentStep === FLOW_STEPS.EMAIL_SIGNUP) {
    content = (
      <EnterpriseSignupContent dispensaryId={dispensaryId ?? undefined} onLoginSuccess={handleLoginFlowSuccess} />
    );
  } else {
    content = (
      <LoyaltyRewards
        enterprise={enterprise}
        rewards={rewards?.getRewardsV2}
        loyaltyConfigData={loyaltyConfigData}
        rewardsDataLoading={rewardsDataLoadingResult}
        loyaltyConfigLoading={loyaltyConfigLoadingResult}
        isEmbedMode={isEmbedMode}
      />
    );
  }

  return isEmbedMode ? <IframeHeightOverride>{content}</IframeHeightOverride> : content;
}
